/**
 * Lunéra bundles — in-card expandable variant dropdowns.
 */
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';
import { formatMoney } from '@theme/money-formatting';
import { bundleDropdowns, renderBundlePickerPanel, findVariant } from '@theme/lunera-bundle-dropdowns';

/** @returns {Theme['routes'] | null} */
function getThemeRoutes() {
  if (typeof Theme !== 'undefined' && Theme.routes) return Theme.routes;
  return null;
}

class LuneraBundles extends HTMLElement {
  connectedCallback() {
    this.data = this.#readProductData();
    this.selectedQty = 5;
    this.selections = [];
    this.isPremium = Boolean(this.querySelector('.bundle'));

    this.updatePrices();
    this.selectBundle(
      this.querySelector('.bundle.active') ||
        this.querySelector('.sb-bundle.selected') ||
        this.querySelector('.bundle') ||
        this.querySelector('.sb-bundle')
    );

    this.querySelectorAll('.bundle').forEach((card) => {
      card.addEventListener('click', () => this.selectBundle(card));
    });

    this.querySelectorAll('.sb-bundle__head').forEach((head) => {
      head.addEventListener('click', () => this.selectBundle(head.closest('.sb-bundle')));
      head.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this.selectBundle(head.closest('.sb-bundle'));
        }
      });
    });

    document.addEventListener('variant:update', () => this.#syncFromMainPicker());
    const variantPicker = document.querySelector('variant-picker');
    if (variantPicker) {
      variantPicker.addEventListener('change', () => setTimeout(() => this.#syncFromMainPicker(), 80));
    }

    this.#patchProductForm();
  }

  getBundleState() {
    return {
      selectedQty: this.selectedQty,
      selections: this.#getSelections(this.selectedQty),
    };
  }

  setItemSelection(index, color, size) {
    this.#setSelection(index, color, size);
  }

  #readProductData() {
    const el = document.getElementById('lunera-product-data');
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch {
      return null;
    }
  }

  /** Aggregated variant lines for cart API (variant id → qty). */
  getCartItems() {
    if (!this.data) return null;

    const selections = this.#getSelections(this.selectedQty);
    const counts = new Map();

    for (const sel of selections) {
      const variant = findVariant(this.data, sel.color, sel.size);
      if (!variant) return null;
      counts.set(variant.id, (counts.get(variant.id) || 0) + 1);
    }

    return [...counts.entries()].map(([id, quantity]) => ({
      id: String(id),
      quantity,
    }));
  }

  #needsBundleBatchAdd() {
    return this.selectedQty > 1 && Boolean(this.getCartItems()?.length);
  }

  #getCartSectionIds() {
    const sectionIds = [];
    document.querySelectorAll('cart-items-component').forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionIds.push(item.dataset.sectionId);
      }
    });
    return sectionIds;
  }

  /** Cart drawer + header listen on document for StandardEvents.cartLinesUpdate. */
  #dispatchCartLinesUpdate(lines, promise) {
    document.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines,
        promise,
      })
    );
  }

  #dispatchCartError(detail) {
    document.dispatchEvent(new CartErrorEvent(detail));
  }

  async #openCartDrawer() {
    if (window.Shopify?.actions?.openCart) {
      await window.Shopify.actions.openCart();
      return;
    }
    document.querySelector('theme-drawer#cart-drawer')?.open?.();
  }

  async #executeSingleAddToCart(form) {
    const routes = getThemeRoutes();
    const cartAddUrl = routes?.cart_add_url || '/cart/add.js';
    const cartUrl = routes?.cart_url || '/cart';
    const sectionIds = this.#getCartSectionIds();

    const variantId = form.querySelector('input[name="id"]')?.value;
    if (!variantId) return;

    const quantity = this.selectedQty > 0 ? this.selectedQty : 1;
    const deferredEventPromise = CartLinesUpdateEvent.createPromise();

    this.#dispatchCartLinesUpdate(
      [{ merchandiseId: String(variantId), quantity }],
      deferredEventPromise.promise
    );

    const payload = {
      id: Number(variantId),
      quantity,
      sections: sectionIds.join(','),
    };

    try {
      const response = await fetch(cartAddUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload),
      });
      const result = await response.json();

      if (result.status) {
        this.#dispatchCartError({
          error: result.message || 'Add to cart failed',
          code: 'INVALID',
          detail: { description: result.description, errors: result.errors },
        });

        const ajaxCart = await fetch(`${cartUrl}.js`).then((r) => r.json());
        deferredEventPromise.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
          detail: { didError: true, items: ajaxCart.items, itemCount: quantity },
        });
        return;
      }

      const ajaxCart = await fetch(`${cartUrl}.js`).then((r) => r.json());
      deferredEventPromise.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
        detail: {
          items: ajaxCart.items,
          itemCount: quantity,
          sections: result.sections,
          didError: false,
        },
      });

      await this.#openCartDrawer();
    } catch (error) {
      deferredEventPromise.reject(error);
      this.#dispatchCartError({
        error: error?.message || 'Network error during add to cart',
        code: 'SERVICE_UNAVAILABLE',
      });
    }
  }

  #patchProductForm() {
    const adbForm = document.getElementById('lun-product-form');
    if (adbForm && adbForm.dataset.lunBundlePatched !== 'true') {
      adbForm.addEventListener('submit', async (event) => {
        event.preventDefault();
        if (!this.data) return;

        this.#syncFormVariantFromFirstSelection();

        if (this.#needsBundleBatchAdd()) {
          const items = this.getCartItems();
          if (!items?.length) return;
          await this.#executeBundleAddToCart(items);
          return;
        }

        await this.#executeSingleAddToCart(adbForm);
      });
      adbForm.dataset.lunBundlePatched = 'true';
    }

    const tryPatch = () => {
      const pf = document.querySelector('product-form-component');
      if (!pf || typeof pf.handleSubmit !== 'function' || pf.dataset.lunBundlePatched === 'true') {
        return pf?.dataset.lunBundlePatched === 'true';
      }

      const original = pf.handleSubmit.bind(pf);

      pf.handleSubmit = (event) => {
        if (!this.data) return original(event);

        this.#syncFormVariantFromFirstSelection();

        if (!this.#needsBundleBatchAdd()) {
          return original(event);
        }

        const items = this.getCartItems();
        if (!items?.length) {
          return original(event);
        }

        event.preventDefault?.();
        this.#executeBundleAddToCart(items);
      };

      pf.dataset.lunBundlePatched = 'true';
      return true;
    };

    const startPatch = () => {
      if (tryPatch()) return;

      const observer = new MutationObserver(() => {
        if (tryPatch()) observer.disconnect();
      });
      observer.observe(document.body, { childList: true, subtree: true });
      setTimeout(() => observer.disconnect(), 15000);
    };

    if (customElements.get('product-form-component')) {
      startPatch();
    } else {
      customElements.whenDefined('product-form-component').then(startPatch);
    }
  }

  /**
   * @param {Array<{id: string, quantity: number}>} items
   */
  async #executeBundleAddToCart(items) {
    const routes = getThemeRoutes();
    const cartAddUrl = routes?.cart_add_url || '/cart/add.js';
    const cartUrl = routes?.cart_url || '/cart';
    const sectionIds = this.#getCartSectionIds();

    const totalQuantity = items.reduce((sum, item) => sum + item.quantity, 0);
    const deferredEventPromise = CartLinesUpdateEvent.createPromise();

    this.#dispatchCartLinesUpdate(
      items.map((item) => ({
        merchandiseId: item.id,
        quantity: item.quantity,
      })),
      deferredEventPromise.promise
    );

    const payload = {
      items: items.map((item) => ({
        id: Number(item.id),
        quantity: item.quantity,
      })),
      sections: sectionIds.join(','),
    };

    try {
      const response = await fetch(cartAddUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload),
      });
      const result = await response.json();

      if (result.status) {
        this.#dispatchCartError({
          error: result.message || 'Add to cart failed',
          code: 'INVALID',
          detail: { description: result.description, errors: result.errors },
        });

        const ajaxCart = await fetch(`${cartUrl}.js`).then((r) => r.json());
        deferredEventPromise.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
          detail: { didError: true, items: ajaxCart.items, itemCount: totalQuantity },
        });
        return;
      }

      const ajaxCart = await fetch(`${cartUrl}.js`).then((r) => r.json());
      deferredEventPromise.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
        detail: {
          items: ajaxCart.items,
          itemCount: totalQuantity,
          sections: result.sections,
          didError: false,
        },
      });

      await this.#openCartDrawer();
    } catch (error) {
      deferredEventPromise.reject(error);
      this.#dispatchCartError({
        error: error?.message || 'Network error during add to cart',
        code: 'SERVICE_UNAVAILABLE',
      });
    }
  }

  #syncFormVariantFromFirstSelection() {
    const sel = this.#getSelections(this.selectedQty)[0];
    if (!sel || !this.data) return;

    const variant = findVariant(this.data, sel.color, sel.size);
    if (!variant) return;

    const input =
      document.querySelector('#lun-variant-id') ||
      document.querySelector('product-form-component input[name="id"]');
    if (input) input.value = String(variant.id);
  }

  #getMainSelection() {
    const swatch = document.querySelector('#swatches .sw.active');
    const sizeBtn = document.querySelector('#sizes .sz.active');
    if (swatch || sizeBtn) {
      return {
        color: swatch?.dataset.name || this.data?.colors[0]?.name,
        size: sizeBtn?.dataset.name || this.data?.sizes[0],
      };
    }

    const picker = document.querySelector('variant-picker');
    if (!picker || !this.data) return { color: this.data.colors[0]?.name, size: this.data.sizes[0] };

    let color = this.data.colors[0]?.name;
    let size = this.data.sizes[0];

    picker.querySelectorAll('fieldset').forEach((fieldset) => {
      const legend = fieldset.querySelector('legend')?.textContent?.trim();
      const checked = fieldset.querySelector('input:checked');
      if (!checked) return;
      if (legend === this.data.colorOption) color = checked.value;
      if (legend === this.data.sizeOption) size = checked.value;
    });

    return { color, size };
  }

  #syncFromMainPicker() {
    const main = this.#getMainSelection();
    if (!this.selections.length) {
      this.selections = Array.from({ length: this.selectedQty }, () => ({ ...main }));
    } else {
      this.selections[0] = { ...main };
    }
    this.#syncFormVariantFromFirstSelection();
    this.#renderPanels();
    this.#dispatchSelectionChange();
  }

  #syncToMainPicker(color, size) {
    const picker = document.querySelector('variant-picker');
    if (!picker || !this.data) return;

    picker.querySelectorAll('fieldset').forEach((fieldset) => {
      const legend = fieldset.querySelector('legend')?.textContent?.trim();
      let targetValue = null;
      if (legend === this.data.colorOption) targetValue = color;
      if (legend === this.data.sizeOption) targetValue = size;
      if (!targetValue) return;

      const input = [...fieldset.querySelectorAll('input[type="radio"]')].find((r) => r.value === targetValue);
      if (input && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });
  }

  #getSelections(qty) {
    if (this.selections.length === qty) return this.selections;
    const main = this.#getMainSelection();
    const base = this.selections.length ? [...this.selections] : [];
    while (base.length < qty) base.push({ ...main });
    return base.slice(0, qty);
  }

  selectBundle(card) {
    if (!card) return;
    bundleDropdowns.close();

    this.querySelectorAll('.bundle, .sb-bundle').forEach((c) => {
      c.classList.remove('active', 'selected');
      c.querySelector('.sb-bundle__head')?.setAttribute('aria-pressed', 'false');
    });
    card.classList.add(this.isPremium ? 'active' : 'selected');
    if (!this.isPremium) {
      card.querySelector('.sb-bundle__head')?.setAttribute('aria-pressed', 'true');
    }

    const qty = Number(card.dataset.bundleQty) || 1;
    this.selectedQty = qty;
    this.selections = this.#getSelections(qty);

    const input =
      document.querySelector('.product-form input[name="quantity"]') ||
      document.querySelector('input[name="quantity"]');
    if (input) {
      input.value = qty;
      input.dispatchEvent(new Event('change', { bubbles: true }));
      input.dispatchEvent(new Event('input', { bubbles: true }));
    }

    this.#syncFormVariantFromFirstSelection();
    this.#renderPanels();
    this.#dispatchSelectionChange();
    this.#dispatchBundlePrices(card);
  }

  #dispatchBundlePrices(card) {
    if (!this.isPremium || !card) return;

    const saveLabel =
      document.getElementById('priceNow')?.dataset.saveLabel ||
      document.getElementById('saveTag')?.dataset.saveLabel ||
      'Du sparar';

    const nowCents = Number(card.dataset.nowCents);
    const wasCents = Number(card.dataset.wasCents);
    const saveCents = Number(card.dataset.saveCents);

    let now = card.querySelector('[data-bundle-price]')?.textContent?.trim() || '';
    let was = card.querySelector('[data-bundle-compare]')?.textContent?.trim() || '';
    let save = '';

    if (Number.isFinite(nowCents) && nowCents > 0) {
      now = this.#formatMoney(nowCents);
    }
    if (Number.isFinite(saveCents) && saveCents > 0) {
      if (Number.isFinite(wasCents) && wasCents > 0) {
        was = this.#formatMoney(wasCents);
      }
      save = `${saveLabel} ${this.#formatMoney(saveCents)}`;
    } else {
      was = '';
      save = '';
    }

    document.dispatchEvent(
      new CustomEvent('lunera:bundle-prices', { detail: { now, was, save } })
    );
  }

  #renderPanels() {
    if (!this.data) return;

    if (this.isPremium) {
      const host = this.querySelector('.lun-bundle-panels');
      if (!host) return;
      host.hidden = this.selectedQty <= 1;
      host.querySelectorAll('[data-cb-bundle-panel]').forEach((panel) => {
        panel.hidden = true;
        panel.innerHTML = '';
      });
      if (this.selectedQty <= 1) return;

      const panel = host.querySelector(`[data-cb-bundle-panel][data-qty="${this.selectedQty}"]`);
      if (!panel) return;
      panel.hidden = false;

      renderBundlePickerPanel({
        container: panel,
        data: this.data,
        selections: this.selections,
        controller: bundleDropdowns,
        onSelect: (index, color, size) => this.#setSelection(index, color, size),
      });
      return;
    }

    this.querySelectorAll('[data-sb-bundle-panel]').forEach((panel) => {
      panel.hidden = true;
      panel.innerHTML = '';
    });

    const selected = this.querySelector('.sb-bundle.selected');
    if (!selected) return;

    const panel = selected.querySelector('[data-sb-bundle-panel]');
    if (!panel) return;

    panel.hidden = false;

    renderBundlePickerPanel({
      container: panel,
      data: this.data,
      selections: this.selections,
      controller: bundleDropdowns,
      onSelect: (index, color, size) => this.#setSelection(index, color, size),
    });

    panel.addEventListener('click', (e) => e.stopPropagation());
  }

  #setSelection(index, color, size) {
    const current = this.selections[index] || this.#getMainSelection();
    this.selections[index] = {
      color: color ?? current.color,
      size: size ?? current.size,
    };

    if (index === 0) {
      this.#syncToMainPicker(this.selections[0].color, this.selections[0].size);
      this.#syncFormVariantFromFirstSelection();
    }

    this.#renderPanels();
    this.#dispatchSelectionChange();
  }

  #dispatchSelectionChange() {
    document.dispatchEvent(
      new CustomEvent('lunera:selection-change', {
        detail: {
          bundleQty: this.selectedQty,
          selections: this.#getSelections(this.selectedQty),
          summary: this.#buildSummary(),
        },
      })
    );
  }

  #buildSummary() {
    const qty = this.selectedQty;
    const sel = this.#getSelections(qty)[0] || this.#getMainSelection();
    const color = sel.color || '';
    const size = sel.size || '';
    if (qty > 1) return `${qty} · ${size} · ${color}`;
    return `${size} · ${color}`;
  }

  getPriceFromDom() {
    const priceEl = document.getElementById('priceNow');
    if (priceEl?.dataset.unitPrice) {
      const unitCents = Number(priceEl.dataset.unitPrice);
      const compareCents = Number(priceEl.dataset.unitCompare || priceEl.dataset.unitPrice);
      if (Number.isFinite(unitCents) && unitCents > 0) {
        return { unitCents, compareCents };
      }
    }

    if (priceEl) {
      const parse = (el) => {
        if (!el) return null;
        const raw = el.textContent.replace(/[^\d,.]/g, '').replace(',', '.');
        const val = parseFloat(raw);
        return Number.isFinite(val) ? Math.round(val * 100) : null;
      };
      const unitCents = parse(priceEl);
      const compareCents = parse(document.getElementById('priceWas')) ?? unitCents;
      if (unitCents) return { unitCents, compareCents };
    }

    const sale = document.querySelector('.product-details .price-item--sale');
    const regular = document.querySelector('.product-details .price-item--regular');
    const compare = document.querySelector('.product-details .price-item--compare');

    const parse = (el) => {
      if (!el) return null;
      const raw = el.textContent.replace(/[^\d,.]/g, '').replace(',', '.');
      const val = parseFloat(raw);
      return Number.isFinite(val) ? Math.round(val * 100) : null;
    };

    const unitCents = parse(sale) ?? parse(regular);
    if (!unitCents) return null;
    return { unitCents, compareCents: parse(compare) ?? unitCents };
  }

  updatePrices() {
    const prices = this.getPriceFromDom();
    if (!prices) return;

    const { unitCents, compareCents } = prices;

    this.querySelectorAll('[data-bundle-price]').forEach((el) => {
      const qty = Number(el.dataset.bundlePrice);
      const cents = this.bundleTotalCents(qty, unitCents);
      el.textContent = this.#formatMoney(cents);
      el.closest('.bundle')?.setAttribute('data-now-cents', String(cents));
    });

    this.querySelectorAll('[data-bundle-compare]').forEach((el) => {
      const qty = Number(el.dataset.bundleCompare);
      const cents = compareCents * qty;
      el.textContent = this.#formatMoney(cents);
      el.closest('.bundle')?.setAttribute('data-was-cents', String(cents));
      el.closest('.bundle')?.setAttribute('data-save-cents', String(Math.max(0, cents - this.bundleTotalCents(qty, unitCents))));
    });

    const active =
      this.querySelector('.bundle.active') ||
      this.querySelector('.sb-bundle.selected') ||
      this.querySelector('.bundle');
    if (active) this.#dispatchBundlePrices(active);
  }

  bundleTotalCents(qty, unitCents) {
    const fiveOff = this.data?.discounts?.fiveOff ?? 55100;
    if (qty === 5) return unitCents * 5 - fiveOff;
    return unitCents * qty;
  }

  #getMoneyFormat() {
    const priceEl = document.getElementById('priceNow');
    return priceEl?.dataset.moneyFormat || '{{amount_with_comma_separator}} kr';
  }

  #getCurrency() {
    const priceEl = document.getElementById('priceNow');
    return priceEl?.dataset.currency || 'SEK';
  }

  #formatMoney(cents) {
    return formatMoney(cents, this.#getMoneyFormat(), this.#getCurrency());
  }

  /** @deprecated use #formatMoney */
  formatMoney(amount) {
    return this.#formatMoney(Math.round(amount * 100));
  }
}

if (!customElements.get('lunera-bundles')) {
  customElements.define('lunera-bundles', LuneraBundles);
}
